import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bar, Line, Pie } from 'react-chartjs-2';
import styles from './Dashboard.module.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  Title,
  Filler,
} from 'chart.js';
import Modal from 'react-modal';
import 'react-datepicker/dist/react-datepicker.css';
import DashboardDateFilter from './dashboard/DashboardDateFilter';
import DashboardHelpModal from './dashboard/DashboardHelpModal';
import DashboardCustomizeModal from './dashboard/DashboardCustomizeModal';
import ProductionDashboardWidgetBody from './dashboard/ProductionDashboardWidgets';
import DashboardSidebar from './dashboard/DashboardSidebar';
import {
  loadDashboardLayout,
  saveDashboardLayout,
} from './dashboard/dashboardLayouts';
import SourceFileSummary from './SourceFileSummary';
import {
  buildFailureRateChartData,
  buildParetoChartData,
  buildPieChartData,
  fillMissingMonths,
  getDefaultDateRange,
  isValidDateRange,
  failureRateChartOptions,
  paretoChartOptions,
  pieChartOptions,
} from './dashboard/dashboardData';
import {
  fetchDashboardDataByDateRange,
  fetchDashboardDataByFilter,
  fetchFailureRateMonthly,
  fetchUniqueSerialCount,
} from './dashboard/dashboardApi';
import { checkForAppUpdate, installAppUpdate } from '../api/appUpdate';

Modal.setAppElement('#root');

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  Title,
  Filler
);

const Dashboard = ({ onLogout, onToggleLanguage, currentUser }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const productionWidgetDefs = useMemo(() => ([
    {
      id: 'pass_fail_pie',
      badge: t('Overview'),
      title: t('Pie Chart'),
      description: t('Keep the global PASS and FAIL balance visible at a glance.'),
    },
    {
      id: 'production_goal',
      badge: t('Output'),
      title: t('Production Goal'),
      description: t('Track total successful production against the target.'),
    },
    {
      id: 'pareto_chart',
      badge: t('Failures'),
      title: t('Pareto Chart Analysis'),
      description: t('Keep the most common failing steps visible on the dashboard.'),
    },
    {
      id: 'monthly_failure_rate',
      badge: t('Trend'),
      title: t('Monthly Failure Rate'),
      description: t('Monitor the trend of failure rate over time.'),
    },
    {
      id: 'control_chart_widget',
      badge: t('Measurements'),
      title: t('Control Chart Analysis'),
      description: 'Preview the most populated numeric step with its current control behavior.',
      path: '/control-chart',
    },
    {
      id: 'uut_status_widget',
      badge: t('Status'),
      title: t('UUT Status Summary'),
      description: 'Compare PASS, FAIL, STOP, and UNCOMPLETE counts by serial number.',
      path: '/uut-status-summary',
    },
    {
      id: 'root_cause_widget',
      badge: t('Prediction'),
      title: t('Root Cause Analyzer'),
      description: 'Keep the strongest candidate steps visible from the case-control root-cause model.',
      path: '/root-cause-prediction',
    },
    {
      id: 'step_frequency_widget',
      badge: t('Flow'),
      title: t('Step Frequency Analyzer'),
      description: 'Surface the steps with the highest fail percentage and execution weight.',
      path: '/step-frequency',
    },
    {
      id: 'root_cause_alarms_widget',
      badge: t('Alarms'),
      title: t('Root Cause Alarms'),
      description: 'Keep the live alarm queue visible for high-severity failure evidence.',
      path: '/root-cause-alarms',
    },
    {
      id: 'ml_runs_widget',
      badge: t('ML'),
      title: t('ML Runs'),
      description: 'Show the latest model run and its predicted PASS/FAIL balance.',
      path: '/ml/runs',
    },
    {
      id: 'process_capability_widget',
      badge: t('Capability'),
      title: t('Process Capability Report'),
      description: 'Preview a numeric measurement distribution with capability markers.',
      path: '/normal-distribution',
    },
    {
      id: 'first_fail_widget',
      badge: t('Root cause'),
      title: t('First-Fail Fingerprint'),
      description: 'Separate primary triggers from later cascade failures.',
      path: '/first-fail-fingerprint',
    },
    {
      id: 'margin_risk_widget',
      badge: t('Root cause'),
      title: t('Limit Margin Risk'),
      description: 'Track pass measurements that are drifting close to specification limits.',
      path: '/limit-margin-risk',
    },
    {
      id: 'hotspots_widget',
      badge: t('Root cause'),
      title: t('Production Hotspots'),
      description: 'Compare stations, fixtures, operators, versions, and hours against baseline failure rate.',
      path: '/production-hotspots',
    },
    {
      id: 'cascade_widget',
      badge: t('Root cause'),
      title: t('Cascade Failure Spread'),
      description: 'Measure which first failures trigger the longest downstream failure chains.',
      path: '/cascade-failure-spread',
    },
    {
      id: 'retry_pressure_widget',
      badge: t('Root cause'),
      title: t('Retry Pressure'),
      description: 'Expose the sections consuming retries, time, and operator effort.',
      path: '/retry-pressure',
    },
    {
      id: 'late_failure_widget',
      badge: t('Root cause'),
      title: t('Late-Stage Failure Cost'),
      description: 'Prioritize failures that happen late in the test flow and waste more cycle time.',
      path: '/late-failure-cost',
    },
  ]), [t]);
  const productionWidgetLayoutDefs = useMemo(() => ([
    { id: 'pass_fail_pie', defaultSize: 'normal' },
    { id: 'production_goal', defaultSize: 'normal' },
    { id: 'pareto_chart', defaultSize: 'wide' },
    { id: 'monthly_failure_rate', defaultSize: 'wide' },
    { id: 'control_chart_widget', defaultSize: 'wide', defaultVisible: false },
    { id: 'uut_status_widget', defaultSize: 'wide', defaultVisible: false },
    { id: 'root_cause_widget', defaultSize: 'wide', defaultVisible: false },
    { id: 'step_frequency_widget', defaultSize: 'wide', defaultVisible: false },
    { id: 'root_cause_alarms_widget', defaultSize: 'wide', defaultVisible: false },
    { id: 'ml_runs_widget', defaultSize: 'normal', defaultVisible: false },
    { id: 'process_capability_widget', defaultSize: 'wide', defaultVisible: false },
    { id: 'first_fail_widget', defaultSize: 'wide', defaultVisible: false },
    { id: 'margin_risk_widget', defaultSize: 'wide', defaultVisible: false },
    { id: 'hotspots_widget', defaultSize: 'wide', defaultVisible: false },
    { id: 'cascade_widget', defaultSize: 'wide', defaultVisible: false },
    { id: 'retry_pressure_widget', defaultSize: 'wide', defaultVisible: false },
    { id: 'late_failure_widget', defaultSize: 'wide', defaultVisible: false },
  ]), []);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [pieChartData, setPieChartData] = useState({ PASS: 0, FAIL: 0 });
  const [paretoChartData, setParetoChartData] = useState(null);
  const [timeFilter] = useState('monthly');
  const [totalProduction, setTotalProduction] = useState(0);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [expandedChart, setExpandedChart] = useState(null);
  const [{ start, end }, setDateRange] = useState(getDefaultDateRange);
  const [errorMessage, setErrorMessage] = useState('');
  const [updateStatus, setUpdateStatus] = useState('');
  const [isUpdatingApp, setIsUpdatingApp] = useState(false);
  const [failureRateData, setFailureRateData] = useState([]);
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const [selectedFamilies, setSelectedFamilies] = useState([]);
  const [widgetLayout, setWidgetLayout] = useState(() =>
    loadDashboardLayout('smarti-production-dashboard-layout', productionWidgetLayoutDefs)
  );
  const [productionGoal, setProductionGoal] = useState(() => {
    const savedGoal = localStorage.getItem('productionGoal');
    return savedGoal ? parseInt(savedGoal, 10) : 50;
  });

  useEffect(() => {
    const loadFailureRateData = async () => {
      try {
        const data = await fetchFailureRateMonthly({ startDate: start, endDate: end, selectedFamilies });
        setFailureRateData(data);
      } catch (err) {
        console.error('Error fetching failure rate data:', err);
      }
    };

    if (start && end) loadFailureRateData();
  }, [end, selectedFamilies, start]);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const data = await fetchDashboardDataByFilter(timeFilter, selectedFamilies);
        setPieChartData(data.pieData);
        setParetoChartData(data.paretoData);
        setTotalProduction(data.totalProduction);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      }
    };

    loadDashboardData();
  }, [selectedFamilies, timeFilter]);

  useEffect(() => {
    const loadUniqueSerialCount = async () => {
      try {
        await fetchUniqueSerialCount();
      } catch (err) {
        console.error('Error fetching unique serial count:', err);
      }
    };

    loadUniqueSerialCount();
  }, []);

  

  const filledFailureRateData = useMemo(
    () => fillMissingMonths(failureRateData, start, end),
    [failureRateData, start, end]
  );
  const failureRateChartData = useMemo(
    () => buildFailureRateChartData(filledFailureRateData),
    [filledFailureRateData]
  );
  const pieData = useMemo(() => buildPieChartData(pieChartData), [pieChartData]);
  const paretoData = useMemo(() => buildParetoChartData(paretoChartData), [paretoChartData]);
  const progressPercentage = productionGoal > 0 ? (totalProduction / productionGoal) * 100 : 0;

  useEffect(() => {
    saveDashboardLayout('smarti-production-dashboard-layout', widgetLayout);
  }, [widgetLayout]);

  const handleProductionGoalChange = (event) => {
    const newGoal = parseInt(event.target.value, 10);
    if (newGoal > 0) {
      setProductionGoal(newGoal);
      localStorage.setItem('productionGoal', newGoal);
    }
  };

  const applyDateFilter = async () => {
    if (!isValidDateRange(start, end)) {
      setErrorMessage('Please choose a valid date range.');
      return;
    }

    try {
      setErrorMessage('');
      const data = await fetchDashboardDataByDateRange({ startDate: start, endDate: end, selectedFamilies });
      setPieChartData(data.pieData);
      setParetoChartData(data.paretoData);
      setTotalProduction(data.totalProduction);
    } catch (err) {
      console.error('Error applying date filter:', err);
    }
  };

  const handleUpdateApp = async () => {
    if (isUpdatingApp) return;
    setIsUpdatingApp(true);
    setUpdateStatus('Checking for updates...');
    try {
      const update = await checkForAppUpdate();

      if (!update.configured) {
        setUpdateStatus(update.message || 'Update manifest is not configured.');
        return;
      }

      if (!update.updateAvailable) {
        setUpdateStatus(`Smarti is up to date (${update.currentVersion}).`);
        return;
      }

      const notes = update.notes ? `\n\nRelease notes:\n${update.notes}` : '';
      const shouldInstall = window.confirm(
        `Smarti ${update.latestVersion} is available. Current version: ${update.currentVersion}.\n\nInstall now? The app will close and restart after the update.${notes}`
      );

      if (!shouldInstall) {
        setUpdateStatus('Update cancelled.');
        return;
      }

      setUpdateStatus('Downloading and verifying update...');
      const result = await installAppUpdate();
      setUpdateStatus(result.message || 'Update started. Smarti will restart.');
    } catch (error) {
      setUpdateStatus(error.message || 'Update failed.');
    } finally {
      setIsUpdatingApp(false);
    }
  };

  const visibleWidgets = widgetLayout.filter((item) => item.visible);

  const renderWidget = (item) => {
    const id = item.id;
    const sizeClass = item.size === 'wide' ? styles.widgetCardWide : '';
    if (id === 'pass_fail_pie') {
      return (
        <section className={`${styles.widgetCard} ${sizeClass}`} key={id}>
          <div className={styles.widgetHeader}>
            <div>
              <span className={styles.widgetBadge}>{t('Overview')}</span>
              <h3>{t('Pie Chart')}</h3>
              <p>{t('Keep the global PASS and FAIL balance visible at a glance.')}</p>
            </div>
            <button type="button" className={styles.chartExpandButton} onClick={() => setExpandedChart('pie')}>
              {t('Expand')}
            </button>
          </div>
          <div className={styles.widgetChartTall}>
            <Pie data={pieData} options={{ ...pieChartOptions, maintainAspectRatio: false }} />
          </div>
        </section>
      );
    }

    if (id === 'pareto_chart') {
      return (
        <section className={`${styles.widgetCard} ${sizeClass}`} key={id}>
          <div className={styles.widgetHeader}>
            <div>
              <span className={styles.widgetBadge}>{t('Failures')}</span>
              <h3>{t('Pareto Chart Analysis')}</h3>
              <p>{t('Keep the most common failing steps visible on the dashboard.')}</p>
            </div>
            <button type="button" className={styles.chartExpandButton} onClick={() => setExpandedChart('pareto')}>
              {t('Expand')}
            </button>
          </div>
          <div className={styles.widgetChartWide}>
            {paretoData ? (
              <Bar data={paretoData} options={paretoChartOptions} />
            ) : (
              <div className={styles.placeholderContent}>
                {Array.isArray(paretoChartData) && paretoChartData.length === 0
                  ? t('No Pareto data available for the selected filter.')
                  : t('Loading Pareto Chart...')}
              </div>
            )}
          </div>
        </section>
      );
    }

    if (id === 'production_goal') {
      return (
        <section className={`${styles.widgetCard} ${sizeClass}`} key={id}>
          <div className={styles.widgetHeader}>
            <div>
              <span className={styles.widgetBadge}>{t('Output')}</span>
              <h3>{t('Production Goal')}</h3>
              <p>{t('Track total successful production against the target.')}</p>
            </div>
          </div>

          <div className={styles.goalWidget}>
            <strong>{totalProduction} pcs.</strong>
            <label className={styles.goalLabel}>
              {t('Set Production Goal')}
              <input
                type="number"
                value={productionGoal}
                onChange={handleProductionGoalChange}
                min="1"
                className={styles.productionGoalInput}
              />
            </label>
            <div className={styles.progressBarContainer}>
              <div
                className={styles.progressBar}
                style={{ width: `${Math.min(progressPercentage, 100)}%` }}
              ></div>
            </div>
            <p className={styles.progressText}>
              {t('Progress')}: {Math.round(progressPercentage)}%
            </p>
          </div>
        </section>
      );
    }

    if (id === 'monthly_failure_rate') {
      return (
        <section className={`${styles.widgetCard} ${sizeClass}`} key={id}>
          <div className={styles.widgetHeader}>
            <div>
              <span className={styles.widgetBadge}>{t('Trend')}</span>
              <h3>{t('Monthly Failure Rate')}</h3>
              <p>{t('Monitor the trend of failure rate over time.')}</p>
            </div>
            <button type="button" className={styles.chartExpandButton} onClick={() => setExpandedChart('failureRate')}>
              {t('Expand')}
            </button>
          </div>
          <div className={styles.widgetChartWide}>
            {filledFailureRateData.length > 0 ? (
              <Line data={failureRateChartData} options={failureRateChartOptions} />
            ) : (
              <p>{t('Loading Chart...')}</p>
            )}
          </div>
        </section>
      );
    }

    const widget = productionWidgetDefs.find((entry) => entry.id === id);
    if (!widget) return null;

    return (
      <section className={`${styles.widgetCard} ${sizeClass}`} key={id}>
        <div className={styles.widgetHeader}>
          <div>
            <span className={styles.widgetBadge}>{widget.badge || t('Analytics')}</span>
            <h3>{widget.title}</h3>
            <p>{widget.description}</p>
          </div>
          <button type="button" className={styles.chartExpandButton} onClick={() => setExpandedChart(id)}>
            {t('Expand')}
          </button>
        </div>
        <ProductionDashboardWidgetBody
          id={id}
          startDate={start}
          endDate={end}
          selectedFamilies={selectedFamilies}
          styles={styles}
          navigate={navigate}
          path={widget.path}
        />
      </section>
    );
  };

  return (
    <div className={styles.containerFluid}>
      <DashboardSidebar
        isOpen={isSidebarOpen}
        onToggleSidebar={() => setIsSidebarOpen((open) => !open)}
        onNavigate={navigate}
        onLogout={onLogout}
        onToggleLanguage={onToggleLanguage}
        currentLanguage={i18n.language}
        onUpdateApp={handleUpdateApp}
        updateStatus={updateStatus}
        isUpdatingApp={isUpdatingApp}
        currentUser={currentUser}
        styles={styles}
        t={t}
      />

      <div className={`${styles.col} ${isSidebarOpen ? styles.dashboardContent : styles.expandedDashboardContent} ${styles.contentAnimation}`}>
        {!isSidebarOpen && (
          <button
            type="button"
            className={styles.floatingSidebarToggle}
            onClick={() => setIsSidebarOpen(true)}
          >
            {t('Show sidebar')}
          </button>
        )}
        <header className={styles.pageHeader}>
          <div>
            <p className={styles.eyebrow}>{t('Production overview')}</p>
            <h1 className={styles.pageTitle}>{t('Smarti Dashboard')}</h1>
            <p className={styles.description}>
              {t('Monitor production quality, failure trends, and the highest-impact test steps from one place.')}
            </p>
          </div>
        </header>

        <DashboardDateFilter
          startDate={start}
          endDate={end}
          errorMessage={errorMessage}
          onStartDateChange={(date) => setDateRange((current) => ({ ...current, start: date }))}
          onEndDateChange={(date) => setDateRange((current) => ({ ...current, end: date }))}
          onApply={applyDateFilter}
          onOpenAlarms={() => navigate('/root-cause-alarms')}
          summaryContent={(
            <SourceFileSummary
              startDate={start}
              endDate={end}
              title={t('Source files used for dashboard charts')}
              className={styles.integratedSourceSummary}
              compact
              selectedFamilies={selectedFamilies}
              onFamiliesChange={setSelectedFamilies}
              actionContent={(
                <button type="button" className={styles.customizeButton} onClick={() => setIsCustomizeOpen(true)}>
                  {t('Customize dashboard')}
                </button>
              )}
            />
          )}
          styles={styles}
          t={t}
        />

        <section className={styles.widgetGrid}>
          {visibleWidgets.map((item) => renderWidget(item))}
        </section>
      </div>

      <DashboardHelpModal
        isOpen={isHelpOpen}
        onOpen={() => setIsHelpOpen(true)}
        onClose={() => setIsHelpOpen(false)}
        styles={styles}
      />
      <DashboardCustomizeModal
        isOpen={isCustomizeOpen}
        onClose={() => setIsCustomizeOpen(false)}
        title={t('Arrange your production widgets')}
        description={t('Show, hide, resize, and reorder your production widgets without leaving the dashboard view. This keeps the workspace clean while still letting each user build the overview they need.')}
        layout={widgetLayout}
        widgetDefs={productionWidgetDefs}
        setLayout={setWidgetLayout}
        t={t}
        tone="production"
      />
      <Modal
        isOpen={Boolean(expandedChart)}
        onRequestClose={() => setExpandedChart(null)}
        className={styles.zoomModalContent}
        overlayClassName={styles.zoomModalOverlay}
        contentLabel="Expanded dashboard chart"
      >
        <button type="button" onClick={() => setExpandedChart(null)} className={styles.closeZoomButton}>
          {t('Close')}
        </button>
        <div className={styles.zoomChartContainer}>
          {expandedChart === 'pie' && (
            <Pie data={pieData} options={{ ...pieChartOptions, maintainAspectRatio: false }} />
          )}
          {expandedChart === 'pareto' && paretoData && (
            <Bar data={paretoData} options={{ ...paretoChartOptions, maintainAspectRatio: false }} />
          )}
          {expandedChart === 'failureRate' && filledFailureRateData.length > 0 && (
            <Line data={failureRateChartData} options={{ ...failureRateChartOptions, maintainAspectRatio: false }} />
          )}
          {expandedChart &&
            !['pie', 'pareto', 'failureRate'].includes(expandedChart) && (
              <ProductionDashboardWidgetBody
                id={expandedChart}
                startDate={start}
                endDate={end}
                selectedFamilies={selectedFamilies}
                styles={styles}
                navigate={navigate}
                path={productionWidgetDefs.find((entry) => entry.id === expandedChart)?.path}
              />
            )}
        </div>
      </Modal>

      
    </div>
  );
};

export default Dashboard;
